import React, { useEffect, useMemo, useState } from "react";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import {
  Ingredient,
  OrderRequest,
  OrderRequestStatus,
  UnitType,
} from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";

interface SupplierOption {
  ID: number;
  Name: string;
}

interface LineDraft {
  Ingredient: string;
  NewProduct: string;
  Supplier: string;
  Quantity: string;
  UnitType: string;
  Notes: string;
}

const emptyLine = (): LineDraft => ({
  Ingredient: "",
  NewProduct: "",
  Supplier: "",
  Quantity: "",
  UnitType: "",
  Notes: "",
});

const PRIORITIES = [
  { value: "low", label: "Niski" },
  { value: "normal", label: "Normalni" },
  { value: "high", label: "Visoki" },
];

/**
 * Order Requests (Zahtjev za narudžbu) - Nabava module
 * Master/detail view over OrderRequest + OrderRequestLine tables.
 */
const OrderRequests = () => {
  savePathToLocalStorage();

  const [requests, setRequests] = useState<OrderRequest[]>([]);
  const [selected, setSelected] = useState<OrderRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Reference data
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [statuses, setStatuses] = useState<OrderRequestStatus[]>([]);

  // Create form state
  const [form, setForm] = useState({
    Title: "",
    RequiredDeliveryDate: new Date().toISOString().split("T")[0],
    Priority: "normal",
    Status: "",
    Notes: "",
  });
  const [lines, setLines] = useState<LineDraft[]>([emptyLine()]);

  useEffect(() => {
    loadRequests();
    loadReferenceData();
  }, []);

  const loadRequests = async () => {
    setLoading(true);
    try {
      const data = await mesApi.getOrderRequests();
      setRequests(data);
    } catch (err: any) {
      toast.error("Greška pri učitavanju zahtjeva: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadReferenceData = async () => {
    try {
      const [ing, sup, units, st] = await Promise.all([
        mesApi.getIngredients(),
        mesApi.getSuppliers({ is_active: true }),
        mesApi.getUnitTypes(),
        mesApi.getOrderRequestStatuses(),
      ]);
      setIngredients(ing || []);
      setSuppliers((sup || []) as SupplierOption[]);
      setUnitTypes(units || []);
      setStatuses(st || []);
    } catch (err: any) {
      toast.error("Greška pri učitavanju šifrarnika: " + err.message);
    }
  };

  const handleSelect = async (request: OrderRequest) => {
    try {
      const detail = await mesApi.getOrderRequest(request.ID);
      setSelected(detail);
    } catch (err: any) {
      toast.error("Greška pri učitavanju detalja: " + err.message);
    }
  };

  const updateLine = (index: number, field: keyof LineDraft, value: string) => {
    setLines((prev) =>
      prev.map((l, i) => (i === index ? { ...l, [field]: value } : l))
    );
  };

  const addLine = () => setLines((prev) => [...prev, emptyLine()]);
  const removeLine = (index: number) =>
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const resetForm = () => {
    setForm({
      Title: "",
      RequiredDeliveryDate: new Date().toISOString().split("T")[0],
      Priority: "normal",
      Status: "",
      Notes: "",
    });
    setLines([emptyLine()]);
  };

  const handleSubmit = async () => {
    if (!form.RequiredDeliveryDate) {
      toast.error("Datum isporuke je obavezan.");
      return;
    }
    const validLines = lines.filter(
      (l) => l.Ingredient || l.NewProduct.trim()
    );
    if (validLines.length === 0) {
      toast.error("Dodajte barem jednu stavku (sirovina ili novi proizvod).");
      return;
    }

    setSaving(true);
    try {
      await mesApi.createOrderRequest({
        Title: form.Title.trim() || null,
        RequiredDeliveryDate: form.RequiredDeliveryDate,
        Priority: form.Priority,
        Status: form.Status ? Number(form.Status) : null,
        Notes: form.Notes.trim() || null,
        Lines: validLines.map((l) => ({
          Ingredient: l.Ingredient ? Number(l.Ingredient) : null,
          NewProduct: l.NewProduct.trim() || null,
          Supplier: l.Supplier ? Number(l.Supplier) : null,
          Quantity: l.Quantity !== "" ? l.Quantity : null,
          UnitType: l.UnitType ? Number(l.UnitType) : null,
          Notes: l.Notes.trim() || null,
        })),
      });
      toast.success("Zahtjev za narudžbu kreiran.");
      setShowModal(false);
      resetForm();
      loadRequests();
    } catch (err: any) {
      toast.error("Greška pri spremanju: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("hr-HR");
  };

  const statusMap = useMemo(
    () => new Map(statuses.map((s) => [s.ID, s.Name])),
    [statuses]
  );

  const priorityLabel = (value?: string | null) =>
    PRIORITIES.find((p) => p.value === value)?.label || value || "-";

  const lineLabel = (line: NonNullable<OrderRequest["Lines"]>[number]) => {
    if (line.IngredientInfo?.IngredientName) return line.IngredientInfo.IngredientName;
    if (line.NewProduct) return line.NewProduct + " (novi)";
    return "Nepoznato";
  };

  return (
    <div className="mes-page order-requests-page">
      <Header title="MES - Zahtjev za narudžbu" />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "1rem" }}>
            {/* Requests list */}
            <div className="mes-section">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "1rem",
                }}
              >
                <h3 style={{ margin: 0, border: "none", padding: 0 }}>Zahtjevi</h3>
                <button className="btn-primary" onClick={() => setShowModal(true)}>
                  + Novi zahtjev
                </button>
              </div>
              {loading ? (
                <div className="loading-state">Učitavanje...</div>
              ) : requests.length === 0 ? (
                <div className="empty-state">
                  <p>Nema zahtjeva za narudžbu.</p>
                </div>
              ) : (
                <div style={{ maxHeight: "calc(100vh - 280px)", overflowY: "auto" }}>
                  {requests.map((req) => (
                    <div
                      key={req.ID}
                      onClick={() => handleSelect(req)}
                      style={{
                        padding: "0.75rem",
                        marginBottom: "0.5rem",
                        background:
                          selected?.ID === req.ID
                            ? "rgba(75, 153, 135, 0.2)"
                            : "rgba(255, 255, 255, 0.02)",
                        borderRadius: "4px",
                        cursor: "pointer",
                        border:
                          selected?.ID === req.ID
                            ? "1px solid #4b9987"
                            : "1px solid transparent",
                      }}
                    >
                      <div style={{ fontWeight: 500 }}>{req.RequestNumber}</div>
                      <div style={{ fontSize: "0.85rem", color: "#adb3bd" }}>
                        {req.Title || "Bez naziva"} · {req.LineCount ?? 0} stavki
                      </div>
                      <div style={{ fontSize: "0.8rem", color: "#adb3bd" }}>
                        Isporuka: {formatDate(req.RequiredDeliveryDate)}
                        {req.StatusInfo?.Name ? ` · ${req.StatusInfo.Name}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Detail */}
            <div className="mes-section">
              {selected ? (
                <>
                  <div style={{ marginBottom: "1rem" }}>
                    <h3 style={{ margin: 0, border: "none", padding: 0 }}>
                      {selected.RequestNumber}
                    </h3>
                    <div style={{ color: "#adb3bd", fontSize: "0.9rem", marginTop: "0.25rem" }}>
                      {selected.Title || "Bez naziva"}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "0.5rem 1.5rem",
                      marginBottom: "1.5rem",
                      fontSize: "0.9rem",
                    }}
                  >
                    <div>
                      <span style={{ color: "#adb3bd" }}>Datum zahtjeva: </span>
                      {formatDate(selected.RequestDate)}
                    </div>
                    <div>
                      <span style={{ color: "#adb3bd" }}>Datum isporuke: </span>
                      {formatDate(selected.RequiredDeliveryDate)}
                    </div>
                    <div>
                      <span style={{ color: "#adb3bd" }}>Prioritet: </span>
                      {priorityLabel(selected.Priority)}
                    </div>
                    <div>
                      <span style={{ color: "#adb3bd" }}>Status: </span>
                      {selected.StatusInfo?.Name ||
                        (selected.Status != null ? statusMap.get(selected.Status) : "-") ||
                        "-"}
                    </div>
                    {selected.WarehouseInfo?.Name && (
                      <div>
                        <span style={{ color: "#adb3bd" }}>Skladište: </span>
                        {selected.WarehouseInfo.Name}
                      </div>
                    )}
                  </div>

                  {selected.Notes && (
                    <div style={{ marginBottom: "1.5rem" }}>
                      <span style={{ color: "#adb3bd" }}>Napomena: </span>
                      {selected.Notes}
                    </div>
                  )}

                  <h4 style={{ marginBottom: "0.5rem" }}>Stavke</h4>
                  {selected.Lines && selected.Lines.length > 0 ? (
                    <div className="mes-table-wrap">
                      <table className="mes-table" style={{ width: "100%" }}>
                      <thead>
                        <tr>
                          <th>Stavka</th>
                          <th>Dobavljač</th>
                          <th style={{ textAlign: "right" }}>Količina</th>
                          <th>Jedinica</th>
                          <th>Napomena</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selected.Lines.map((line) => (
                          <tr key={line.ID}>
                            <td>
                              {lineLabel(line)}
                              {line.Link && (
                                <>
                                  {" "}
                                  <a href={line.Link} target="_blank" rel="noreferrer">
                                    🔗
                                  </a>
                                </>
                              )}
                            </td>
                            <td>{line.SupplierInfo?.Name || "-"}</td>
                            <td style={{ textAlign: "right" }}>
                              {line.Quantity != null ? String(line.Quantity) : "-"}
                            </td>
                            <td>{line.UnitTypeInfo?.Code || "-"}</td>
                            <td>{line.Notes || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <p>Zahtjev nema stavki.</p>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <p>Odaberite zahtjev za prikaz detalja.</p>
                </div>
              )}
            </div>
          </div>

          {/* Create modal */}
          {showModal && (
            <div
              className="mes-modal-overlay"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
            >
              <div
                className="mes-modal"
                style={{ maxWidth: "760px", width: "90%" }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <div className="modal-title-group">
                    <div className="modal-icon">🛒</div>
                    <div className="modal-title-text">
                      <h3>NOVI ZAHTJEV ZA NARUDŽBU</h3>
                      <p className="modal-subtitle">Nabava - kreiranje zahtjeva sa stavkama</p>
                    </div>
                  </div>
                  <button
                    className="close-btn"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                  >
                    ✕
                  </button>
                </div>
                <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(2, 1fr)",
                      gap: "1rem",
                    }}
                  >
                    <div className="form-group">
                      <label>Naziv zahtjeva</label>
                      <input
                        type="text"
                        value={form.Title}
                        onChange={(e) => setForm({ ...form, Title: e.target.value })}
                        placeholder="npr. Tjedna nabava sirovina"
                      />
                    </div>
                    <div className="form-group">
                      <label>Datum isporuke *</label>
                      <input
                        type="date"
                        value={form.RequiredDeliveryDate}
                        onChange={(e) =>
                          setForm({ ...form, RequiredDeliveryDate: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label>Prioritet</label>
                      <select
                        value={form.Priority}
                        onChange={(e) => setForm({ ...form, Priority: e.target.value })}
                      >
                        {PRIORITIES.map((p) => (
                          <option key={p.value} value={p.value}>
                            {p.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Status</label>
                      <select
                        value={form.Status}
                        onChange={(e) => setForm({ ...form, Status: e.target.value })}
                      >
                        <option value="">-</option>
                        {statuses.map((s) => (
                          <option key={s.ID} value={s.ID}>
                            {s.Name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Napomena</label>
                    <textarea
                      value={form.Notes}
                      onChange={(e) => setForm({ ...form, Notes: e.target.value })}
                      rows={2}
                    />
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      margin: "1rem 0 0.5rem",
                    }}
                  >
                    <h4 style={{ margin: 0 }}>Stavke</h4>
                    <button className="btn-primary" onClick={addLine}>
                      + Dodaj stavku
                    </button>
                  </div>

                  {lines.map((line, index) => (
                    <div
                      key={index}
                      style={{
                        border: "1px solid rgba(255,255,255,0.08)",
                        borderRadius: "6px",
                        padding: "0.75rem",
                        marginBottom: "0.75rem",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          gap: "0.75rem",
                        }}
                      >
                        <div className="form-group">
                          <label>Sirovina</label>
                          <select
                            value={line.Ingredient}
                            onChange={(e) => updateLine(index, "Ingredient", e.target.value)}
                          >
                            <option value="">- (ili upiši novi proizvod) -</option>
                            {ingredients.map((ing) => (
                              <option key={ing.ID} value={ing.ID}>
                                {ing.IngredientName}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Novi proizvod (slobodan unos)</label>
                          <input
                            type="text"
                            value={line.NewProduct}
                            onChange={(e) => updateLine(index, "NewProduct", e.target.value)}
                            placeholder="ako nije u šifrarniku"
                            disabled={!!line.Ingredient}
                          />
                        </div>
                        <div className="form-group">
                          <label>Dobavljač</label>
                          <select
                            value={line.Supplier}
                            onChange={(e) => updateLine(index, "Supplier", e.target.value)}
                          >
                            <option value="">-</option>
                            {suppliers.map((s) => (
                              <option key={s.ID} value={s.ID}>
                                {s.Name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
                          <div className="form-group">
                            <label>Količina</label>
                            <input
                              type="number"
                              step="0.001"
                              value={line.Quantity}
                              onChange={(e) => updateLine(index, "Quantity", e.target.value)}
                            />
                          </div>
                          <div className="form-group">
                            <label>Jedinica</label>
                            <select
                              value={line.UnitType}
                              onChange={(e) => updateLine(index, "UnitType", e.target.value)}
                            >
                              <option value="">-</option>
                              {unitTypes.map((u) => (
                                <option key={u.ID} value={u.ID}>
                                  {u.Code}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                      <div className="form-group" style={{ marginTop: "0.5rem" }}>
                        <label>Napomena stavke</label>
                        <input
                          type="text"
                          value={line.Notes}
                          onChange={(e) => updateLine(index, "Notes", e.target.value)}
                        />
                      </div>
                      {lines.length > 1 && (
                        <button
                          className="btn-cancel"
                          style={{ marginTop: "0.25rem" }}
                          onClick={() => removeLine(index)}
                        >
                          Ukloni stavku
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="modal-footer">
                  <button
                    className="btn-cancel"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                  >
                    Odustani
                  </button>
                  <button className="btn-submit" onClick={handleSubmit} disabled={saving}>
                    {saving ? "Spremanje..." : "Spremi zahtjev"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ToastContainer
        autoClose={5000}
        position="bottom-right"
        hideProgressBar={false}
        theme="colored"
      />
    </div>
  );
};

export default OrderRequests;
